const React = require("react");
const {
  variantIdsOf, selectedVariantIds, isVariantSelected, toggleVariant, selectAllVariants, clearVariants,
} = require("../../cjShippingDestinations");
const h = React.createElement;

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

// ONE global variant selection, shared by every destination country. "Select
// all" selects the complete variant list of the product (not only the rows
// matching the search box). There is no limit on the number of variants.
const CJVariantMultiSelector = ({ variants, selection, onChange, t, loading = false, error = "", onRetry, disabled = false }) => {
  const [query, setQuery] = React.useState("");
  const allIds = variantIdsOf(variants);
  const selectedCount = selectedVariantIds(selection, variants).length;
  const total = allIds.length;
  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && !allSelected;
  const masterRef = React.useRef(null);
  React.useEffect(() => { if (masterRef.current) masterRef.current.indeterminate = someSelected; }, [someSelected]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? variants.filter((v) => `${v.name || ""} ${v.sku || ""} ${v.externalVariantId || ""}`.toLowerCase().includes(q))
    : variants;

  return h("div", { className: "cjms-variants" },
    h("div", { className: "cjms-section-head" },
      h("label", { className: "cjms-label" }, t("h_cjms_variants", "Variants (same for every country)")),
      h("span", { className: `cjms-count${selectedCount === 0 && total ? " cjms-count--empty" : ""}`, "aria-live": "polite" },
        t("h_cjms_variants_count", "{count} of {total} variants selected", { count: selectedCount, total }))
    ),
    loading ? h("p", { className: "cjds-muted", role: "status" }, t("h_cjms_variants_loading", "Loading variants...")) : null,
    error ? h("div", { className: "cjds-alert", role: "alert" },
      h("p", null, error),
      onRetry ? h("button", { type: "button", className: "cjds-btn secondary", onClick: onRetry }, t("h_cj_shipping_retry", "Retry")) : null
    ) : null,
    !loading && !error && total === 0 ? h("p", { className: "cjds-muted" }, t("h_cjms_no_variants", "This product has no separate variants.")) : null,
    total > 0 ? h(React.Fragment, null,
      h("div", { className: "cjms-variant-toolbar" },
        h("label", { className: "cjms-check cjms-check--master" },
          h("input", {
            ref: masterRef,
            type: "checkbox",
            checked: allSelected,
            disabled,
            onChange: () => onChange(allSelected ? clearVariants() : selectAllVariants()),
          }),
          t("h_cjms_select_all_variants", "Select all variants")
        ),
        h("div", { className: "cjms-variant-actions" },
          h("button", { type: "button", className: "cjds-btn secondary cjms-btn-sm", disabled: disabled || allSelected, onClick: () => onChange(selectAllVariants()) }, t("h_cjms_select_all", "Select all")),
          h("button", { type: "button", className: "cjds-btn secondary cjms-btn-sm", disabled: disabled || selectedCount === 0, onClick: () => onChange(clearVariants()) }, t("h_cjms_clear_all", "Clear all"))
        ),
        total > 8 ? h("input", {
          type: "search",
          className: "cjms-variant-search",
          value: query,
          placeholder: t("h_cjms_search_variants", "Search variants"),
          "aria-label": t("h_cjms_search_variants", "Search variants"),
          onChange: (e) => setQuery(e.target.value),
        }) : null
      ),
      h("div", { className: "cjms-variant-list", role: "group", "aria-label": t("h_cjms_variants", "Variants (same for every country)") },
        ...visible.map((variant) => {
          const id = String(variant.externalVariantId);
          const checked = isVariantSelected(selection, id);
          return h("label", { key: id, className: `cjms-variant${checked ? " cjms-variant--on" : ""}` },
            h("input", { type: "checkbox", checked, disabled, onChange: () => onChange(toggleVariant(selection, id, variants)) }),
            variant.image ? h("img", { src: variant.image, alt: "", loading: "lazy" }) : h("span", { className: "cjms-variant__noimg" }),
            h("span", { className: "cjms-variant__name" },
              h("strong", null, variant.name || variant.sku || id),
              variant.sku ? h("small", null, variant.sku) : null
            ),
            h("span", { className: "cjms-variant__meta" },
              h("span", null, money(variant.costPrice)),
              h("small", null, t("h_cjms_stock", "Stock {count}", { count: Number(variant.stockQuantity) || 0 }))
            )
          );
        }),
        q && !visible.length ? h("p", { className: "cjds-muted" }, t("h_cjms_no_match", "No variant matches this search.")) : null
      ),
      selectedCount === 0 ? h("p", { className: "cjds-field-error", role: "alert" }, t("h_cjms_select_one", "Select at least one variant.")) : null
    ) : null
  );
};

module.exports = { CJVariantMultiSelector };
